/* eslint-disable react/forbid-prop-types */
import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import {
   GhostButton,
   IconButton,
   PrimaryButton,
} from '../../../../styles/ui/Button';
import { toast } from 'react-toastify';

import Modal from '../../../../components/Modal/Modal';
import { updateItemServices } from '../../../../services/routing/routingScheduleApi';
import { indexCatalogById, suggestedDurationMinutes } from '../serviceFields';

// =============================================================================
// ServiceClassificationModal — atribui os serviços de um item (handoff 0039 §3).
//
// O item carrega uma seleção N:N `services[]` do catálogo unificado. O operador
// ADICIONA serviços (do catálogo ativo) e ajusta a quantidade de cada um. A
// seleção é REPLACE-SET: o que está na lista vira a seleção inteira do item.
// O sistema sugere o tempo de atendimento = Σ(quantity × tempo-do-catálogo); o
// operador pode personalizar (override → estimated_duration_minutes).
//
// Regras espelhadas do backend:
//   - estimated_duration_minutes (override) sem ≥1 serviço → 422
//     duration_without_service.
//   - commission_table_id repetido → 422 duplicate_service (prevenido na UI).
//   - serviço inativo/inexistente → 422 invalid_service.
// =============================================================================

const Body = styled.div`
   display: flex;
   flex-direction: column;
   gap: 14px;
   font-size: 0.85rem;
   color: ${({ theme }) => theme?.colors?.textPrimary ?? '#222'};
`;

const Intro = styled.p`
   margin: 0;
   font-size: 0.8rem;
   line-height: 1.5;
   color: ${({ theme }) => theme?.colors?.textSecondary ?? '#555'};
`;

const AddRow = styled.div`
   display: flex;
   gap: 8px;
   align-items: center;

   select {
      flex: 1;
      padding: 8px 10px;
      font-size: 0.85rem;
      border-radius: 8px;
      border: 1px solid rgba(0, 0, 0, 0.15);
      font-family: inherit;
      background: #fff;
   }
`;

const SelectedList = styled.div`
   display: flex;
   flex-direction: column;
   border: 1px solid var(--line, #e2e2dc);
   border-radius: 10px;
   overflow: hidden;
`;

const ServiceRow = styled.div`
   display: flex;
   align-items: center;
   justify-content: space-between;
   gap: 12px;
   padding: 10px 12px;
   border-bottom: 1px solid rgba(0, 0, 0, 0.06);

   &:last-of-type {
      border-bottom: 0;
   }
`;

const ServiceLabel = styled.div`
   display: flex;
   flex-direction: column;
   gap: 2px;
   min-width: 0;

   .nm {
      font-weight: 600;
      color: #1f1f18;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
   }
   .std {
      font-size: 0.72rem;
      color: #8b8b82;
   }
   .inactive {
      color: #b45309;
   }
`;

const RowRight = styled.div`
   display: flex;
   align-items: center;
   gap: 8px;
   flex-shrink: 0;
`;

const Stepper = styled.div`
   display: inline-flex;
   align-items: center;
   gap: 0;
   border: 1px solid rgba(0, 0, 0, 0.15);
   border-radius: 8px;
   overflow: hidden;

   button {
      width: 30px;
      height: 30px;
      border: 0;
      background: #f5f5f3;
      cursor: pointer;
      font-size: 1.1rem;
      line-height: 1;
      color: #1f1f18;

      &:hover:not(:disabled) {
         background: #ececde;
      }
      &:disabled {
         opacity: 0.4;
         cursor: not-allowed;
      }
   }

   input {
      width: 44px;
      height: 30px;
      border: 0;
      border-left: 1px solid rgba(0, 0, 0, 0.12);
      border-right: 1px solid rgba(0, 0, 0, 0.12);
      text-align: center;
      font-family: 'JetBrains Mono', ui-monospace, monospace;
      font-size: 0.85rem;
      -moz-appearance: textfield;
   }
   input::-webkit-outer-spin-button,
   input::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
   }
`;

const RemoveButton = styled(IconButton).attrs({ $intent: 'danger' })`
   width: 28px;
   height: 28px;
   font-size: 1.1rem;
   color: ${({ theme }) => theme.colors.danger};
`;

const EmptyBox = styled.div`
   padding: 14px 12px;
   font-size: 0.78rem;
   color: #8b8b82;
   font-style: italic;
   text-align: center;
`;

const DurationBox = styled.div`
   display: flex;
   flex-direction: column;
   gap: 8px;
   background: #f5f5f3;
   border: 1px solid var(--line, #e2e2dc);
   border-radius: 10px;
   padding: 12px;
`;

const DurationHead = styled.div`
   display: flex;
   align-items: center;
   justify-content: space-between;
   gap: 12px;
   font-size: 0.78rem;
   color: #44443d;

   strong {
      font-family: 'JetBrains Mono', ui-monospace, monospace;
      color: #1f1f18;
   }
`;

const OverrideRow = styled.label`
   display: flex;
   align-items: center;
   gap: 8px;
   font-size: 0.8rem;
   color: #44443d;
   cursor: pointer;

   input {
      width: 15px;
      height: 15px;
      cursor: pointer;
   }
`;

const DurationInput = styled.div`
   display: flex;
   align-items: center;
   gap: 8px;

   input {
      width: 90px;
      padding: 7px 10px;
      border-radius: 8px;
      border: 1px solid rgba(0, 0, 0, 0.15);
      font-family: 'JetBrains Mono', ui-monospace, monospace;
      font-size: 0.85rem;
   }
   .unit {
      font-size: 0.8rem;
      color: #8b8b82;
   }
   .reset {
      border: 0;
      background: transparent;
      color: #b53636;
      font-size: 0.75rem;
      font-weight: 600;
      cursor: pointer;
      padding: 0;
   }
`;

const ErrBox = styled.div`
   background: rgba(211, 47, 47, 0.08);
   border: 1px solid rgba(211, 47, 47, 0.4);
   border-radius: 8px;
   padding: 9px 12px;
   font-size: 0.78rem;
   color: #b71c1c;
   line-height: 1.5;
`;

function clampQty(value) {
   const n = Math.trunc(Number(value));
   if (Number.isNaN(n) || n < 1) return 1;
   return Math.min(n, 999);
}

function clampMinutes(value) {
   const n = Math.trunc(Number(value));
   if (Number.isNaN(n) || n < 0) return 0;
   return Math.min(n, 99999);
}

export default function ServiceClassificationModal({
   stop,
   catalog = [],
   onClose,
   onSaved = null,
}) {
   const catalogById = useMemo(() => indexCatalogById(catalog), [catalog]);

   // Serviços ativos do catálogo (universo do seletor "adicionar").
   const activeCatalog = useMemo(
      () => (catalog || []).filter((e) => e.is_active !== false),
      [catalog]
   );

   // Seleção inicial — do item adaptado (stop.services). Cada entrada carrega
   // commission_table_id + code + descrição + quantity (vindos do snapshot).
   const [selected, setSelected] = useState(() =>
      (stop?.services || []).map((s) => ({
         commission_table_id: s.commission_table_id,
         service_code: s.service_code,
         service_description: s.service_description,
         quantity: clampQty(s.quantity),
      }))
   );
   const [addValue, setAddValue] = useState('');

   const selectedIds = useMemo(
      () => new Set(selected.map((s) => s.commission_table_id)),
      [selected]
   );

   // Opções disponíveis no seletor = ativos ainda não selecionados.
   const addOptions = useMemo(
      () => activeCatalog.filter((e) => !selectedIds.has(e.id)),
      [activeCatalog, selectedIds]
   );

   const total = useMemo(
      () => selected.reduce((acc, s) => acc + (s.quantity || 0), 0),
      [selected]
   );
   const suggestion = useMemo(
      () => suggestedDurationMinutes(selected, catalogById),
      [selected, catalogById]
   );

   // Override: liga personalização do tempo. Inicia ligado se o item já grava
   // uma duração diferente da sugestão da seleção atual.
   const initialEstimated = stop?.estimatedDuration ?? null;
   const initialSuggestion = useMemo(
      () => suggestedDurationMinutes(stop?.services || [], catalogById),
      [stop, catalogById]
   );
   const [isOverride, setIsOverride] = useState(
      () =>
         initialEstimated != null &&
         Number(initialEstimated) !== Number(initialSuggestion)
   );
   const [overrideValue, setOverrideValue] = useState(() =>
      initialEstimated != null ? String(initialEstimated) : ''
   );

   const [isSaving, setIsSaving] = useState(false);
   const [error, setError] = useState(null);

   const handleAdd = (e) => {
      const id = Number(e.target.value);
      setAddValue('');
      if (!id) return;
      const entry = catalogById[id];
      if (!entry || selectedIds.has(id)) return;
      setSelected((prev) => [
         ...prev,
         {
            commission_table_id: entry.id,
            service_code: entry.service_code,
            service_description: entry.service_description,
            quantity: 1,
         },
      ]);
   };

   const setQty = (id, value) => {
      setSelected((prev) =>
         prev.map((s) =>
            s.commission_table_id === id
               ? { ...s, quantity: clampQty(value) }
               : s
         )
      );
   };
   const bumpQty = (id, delta) => {
      setSelected((prev) =>
         prev.map((s) =>
            s.commission_table_id === id
               ? { ...s, quantity: clampQty((s.quantity || 0) + delta) }
               : s
         )
      );
   };
   const removeService = (id) => {
      setSelected((prev) => prev.filter((s) => s.commission_table_id !== id));
   };

   const handleToggleOverride = (checked) => {
      setIsOverride(checked);
      if (checked && overrideValue.trim() === '') {
         setOverrideValue(String(suggestion));
      }
   };

   const handleSave = async () => {
      setError(null);
      if (isOverride && selected.length === 0) {
         setError(
            'Adicione ao menos um serviço antes de personalizar o tempo de atendimento.'
         );
         return;
      }
      if (isOverride && clampMinutes(overrideValue) <= 0) {
         setError('Informe um tempo de atendimento maior que zero.');
         return;
      }

      setIsSaving(true);
      try {
         const scheduleItem = await updateItemServices(stop.itemId, {
            services: selected.map((s) => ({
               commission_table_id: s.commission_table_id,
               quantity: s.quantity,
            })),
            estimatedDurationMinutes:
               isOverride && selected.length > 0
                  ? clampMinutes(overrideValue)
                  : null,
         });
         toast.success('Serviços do item atualizados.');
         onSaved?.({ itemId: stop.itemId, scheduleItem });
         onClose();
      } catch (err) {
         const code = err?.response?.data?.errors?.[0]?.code;
         const apiMessage =
            err?.response?.data?.errors?.[0]?.message ??
            err?.response?.data?.message;
         const byCode = {
            duration_without_service:
               'Não há tempo de atendimento sem serviço — adicione ao menos um serviço.',
            duplicate_service: 'Há um serviço repetido na seleção.',
            invalid_service:
               'Um dos serviços selecionados está inativo ou não existe mais.',
         };
         setError(apiMessage ?? byCode[code] ?? 'Não foi possível salvar os serviços do item.');
         setIsSaving(false);
      }
   };

   return (
      <Modal
         isOpen
         onClose={isSaving ? () => {} : onClose}
         title="Atribuir serviços"
         maxWidth="500px"
         footer={
            <>
               <GhostButton type="button" onClick={onClose} disabled={isSaving}>
                  Cancelar
               </GhostButton>
               <PrimaryButton
                  type="button"
                  onClick={handleSave}
                  disabled={isSaving}
               >
                  {isSaving ? 'Salvando…' : 'Salvar serviços'}
               </PrimaryButton>
            </>
         }
      >
         <Body>
            <Intro>
               Atribua os serviços deste atendimento a partir do catálogo. O
               tempo sugerido vem do catálogo (tempo por serviço) e ancora os
               ETAs da rota.
            </Intro>

            <AddRow>
               <select
                  value={addValue}
                  onChange={handleAdd}
                  disabled={isSaving || addOptions.length === 0}
               >
                  <option value="">
                     {addOptions.length === 0
                        ? 'Nenhum serviço disponível para adicionar'
                        : '+ Adicionar serviço do catálogo…'}
                  </option>
                  {addOptions.map((e) => (
                     <option key={e.id} value={e.id}>
                        {e.service_code} — {e.service_description}
                     </option>
                  ))}
               </select>
            </AddRow>

            <SelectedList>
               {selected.length === 0 ? (
                  <EmptyBox>Nenhum serviço atribuído.</EmptyBox>
               ) : (
                  selected.map((s) => {
                     const entry = catalogById[s.commission_table_id];
                     const time = entry?.estimated_completion_service_time;
                     const isInactive = entry ? entry.is_active === false : true;
                     return (
                        <ServiceRow key={s.commission_table_id}>
                           <ServiceLabel>
                              <span className="nm">{s.service_description}</span>
                              <span className="std">
                                 {s.service_code}
                                 {time != null
                                    ? ` · ${time} min/un`
                                    : ' · tempo não definido'}
                                 {isInactive && (
                                    <span className="inactive"> · inativo</span>
                                 )}
                              </span>
                           </ServiceLabel>
                           <RowRight>
                              <Stepper>
                                 <button
                                    type="button"
                                    onClick={() =>
                                       bumpQty(s.commission_table_id, -1)
                                    }
                                    disabled={isSaving || s.quantity <= 1}
                                    aria-label={`Diminuir ${s.service_description}`}
                                 >
                                    −
                                 </button>
                                 <input
                                    type="number"
                                    min="1"
                                    value={s.quantity}
                                    onChange={(e) =>
                                       setQty(
                                          s.commission_table_id,
                                          e.target.value
                                       )
                                    }
                                    disabled={isSaving}
                                    aria-label={s.service_description}
                                 />
                                 <button
                                    type="button"
                                    onClick={() =>
                                       bumpQty(s.commission_table_id, 1)
                                    }
                                    disabled={isSaving}
                                    aria-label={`Aumentar ${s.service_description}`}
                                 >
                                    +
                                 </button>
                              </Stepper>
                              <RemoveButton
                                 type="button"
                                 onClick={() =>
                                    removeService(s.commission_table_id)
                                 }
                                 disabled={isSaving}
                                 aria-label={`Remover ${s.service_description}`}
                                 title="Remover serviço"
                              >
                                 ×
                              </RemoveButton>
                           </RowRight>
                        </ServiceRow>
                     );
                  })
               )}
            </SelectedList>

            <DurationBox>
               <DurationHead>
                  <span>Tempo sugerido (catálogo)</span>
                  <strong>{suggestion} min</strong>
               </DurationHead>
               <OverrideRow>
                  <input
                     type="checkbox"
                     checked={isOverride}
                     disabled={isSaving || selected.length === 0}
                     onChange={(e) => handleToggleOverride(e.target.checked)}
                  />
                  Personalizar o tempo de atendimento
               </OverrideRow>
               {isOverride && (
                  <DurationInput>
                     <input
                        type="number"
                        min="1"
                        value={overrideValue}
                        onChange={(e) => setOverrideValue(e.target.value)}
                        disabled={isSaving}
                        aria-label="Tempo de atendimento personalizado"
                     />
                     <span className="unit">min</span>
                     <button
                        type="button"
                        className="reset"
                        onClick={() => setOverrideValue(String(suggestion))}
                        disabled={isSaving}
                     >
                        ↺ usar sugestão ({suggestion} min)
                     </button>
                  </DurationInput>
               )}
               {!isOverride && total > 0 && (
                  <DurationHead>
                     <span>Tempo de atendimento</span>
                     <strong>{suggestion} min</strong>
                  </DurationHead>
               )}
            </DurationBox>

            {error && <ErrBox>{error}</ErrBox>}
         </Body>
      </Modal>
   );
}

ServiceClassificationModal.propTypes = {
   stop: PropTypes.object.isRequired,
   catalog: PropTypes.array,
   onClose: PropTypes.func.isRequired,
   // ({ itemId, scheduleItem }) — update otimista no board.
   onSaved: PropTypes.func,
};
